import axios, { AxiosRequestConfig } from 'axios';
import { useAuthContext } from '../context/AuthContext';
import { useEffect } from 'react';

export const axiosInstance = axios.create({
  baseURL: window.env.REACT_APP_REPORT_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// interceptor per loggare tutte le richieste e aggiungere il token
export const useAxiosInterceptor = () => {
  const auth = useAuthContext();

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.request.use((config) => {
      const token = auth.user?.access_token;
      if (token) (config.headers as any)['Authorization'] = `Bearer ${token}`;
    //  console.log('💡 Richiesta Axios:', config.url, config.method, config.headers);
      return config;
    });

    return () => {
      axiosInstance.interceptors.request.eject(interceptor);
    };
  }, [auth]);
};


export type ApiResponse<T> = {
  data?: T;
  error?: string;
  statusCode?: number;
};

/**
 * Errore API che conserva lo status HTTP, cosi' i chiamanti possono distinguere
 * i casi che hanno una gestione dedicata (es. 409 "Sources not ready" sulla
 * creazione di un report BE) da un errore generico.
 */
export class ApiError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Costruisce un ApiError da una ApiResponse fallita. */
export const toApiError = (res: { error?: string; statusCode?: number }, fallback: string): ApiError =>
  new ApiError(res.error || fallback, res.statusCode);

const getErrorMessage = (error: any): string => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const { data, status } = error.response;
      if (data && Array.isArray(data.detail) && data.detail.length > 0 && data.detail[0].msg) {
        return data.detail.map((err: any) => `${err.loc.join('.')}: ${err.msg}`).join('; ');
      }
      // FastAPI HTTPException: `detail` e' una stringa scritta per l'utente
      // (409 sorgenti non pronte, 400 sulla registrazione fonte, ...). Va mostrata cosi' com'e'.
      if (data && typeof data.detail === 'string' && data.detail.trim()) return data.detail;
      if (data && (data.message || data.error)) return data.message || data.error;
      if (typeof data === 'string') return data;
      return `Errore HTTP ${status}: ${error.response.statusText || 'Errore sconosciuto dal server'}`;
    } else if (error.request) {
      return 'Nessuna risposta dal server. Controlla la tua connessione.';
    } else {
      return `Errore nella configurazione della richiesta: ${error.message}`;
    }
  } else if (error instanceof Error) {
    return error.message;
  }
  return 'Si è verificato un errore inatteso.';
};

// GET / POST / PUT / DELETE
export const getWithErrorData = async <T>(url: string, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> => {
  try {
    const response = await axiosInstance.get<T>(url, {
      ...config,
      headers: { Accept: 'application/json', ...(config.headers || {}) },
    });
    return { data: response.data, statusCode: response.status };
  } catch (error: any) {
    return { error: getErrorMessage(error), statusCode: error.response?.status };
  }
};


export const getWithErrorDataBlob = async <T>(
  url: string,
  config: AxiosRequestConfig = {}
): Promise<ApiResponse<T>> => {
  try {
    const response = await axiosInstance.get<T>(url, {
      ...config,
      headers: {
        ...(config.responseType === 'blob'
          ? {} // 👈 non mettere JSON se scarichi file
          : { Accept: 'application/json' }),
        ...(config.headers || {}),
      },
    });
    return { data: response.data, statusCode: response.status };
  } catch (error: any) {
    return { error: getErrorMessage(error), statusCode: error.response?.status };
  }
};

export const postWithErrorData = async <T>(url: string, payload: unknown, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> => {
  try {
    const response = await axiosInstance.post<T>(url, payload, {
      ...config,
      headers: { Accept: 'application/json', ...(config.headers || {}) },
    });
    return { data: response.data, statusCode: response.status };
  } catch (error: any) {
    return { error: getErrorMessage(error), statusCode: error.response?.status };
  }
};

// POST senza body
export const postWithoutBody = async <T>(
  url: string,
  config: AxiosRequestConfig = {}
): Promise<ApiResponse<T>> => {
  try {
    const response = await axiosInstance.post<T>(url, undefined, {
      ...config,
      headers: { Accept: 'application/json', ...(config.headers || {}) },
    });
    return { data: response.data, statusCode: response.status };
  } catch (error: any) {
    return { error: getErrorMessage(error), statusCode: error.response?.status };
  }
};


export const putWithErrorData = async <T>(url: string, payload: unknown, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> => {
  try {
    const response = await axiosInstance.put<T>(url, payload, {
      ...config,
      headers: { Accept: 'application/json', ...(config.headers || {}) },
    });
    return { data: response.data, statusCode: response.status };
  } catch (error: any) {
    return { error: getErrorMessage(error), statusCode: error.response?.status };
  }
};

export const deleteWithErrorData = async <T>(url: string, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> => {
  try {
    const response = await axiosInstance.delete<T>(url, {
      ...config,
      headers: { Accept: 'application/json', ...(config.headers || {}) },
    });
    return { data: response.data, statusCode: response.status };
  } catch (error: any) {
    return { error: getErrorMessage(error), statusCode: error.response?.status };
  }
};

export const postMultipartWithErrorData = async <T>(
  url: string,
  formData: FormData,
  config: AxiosRequestConfig = {},
): Promise<ApiResponse<T>> => {
  try {
    const response = await axiosInstance.post<T>(url, formData, {
      ...config,
      headers: {
        Accept: 'application/json',
        ...(config.headers || {}),
        // Forza axios a calcolare il Content-Type con boundary (sovrascrive il default JSON).
        'Content-Type': 'multipart/form-data',
      },
      // Evita la serializzazione JSON del default axios: passa FormData cosi com'è.
      transformRequest: (data) => data,
    });
    return { data: response.data, statusCode: response.status };
  } catch (error: any) {
    return { error: getErrorMessage(error), statusCode: error.response?.status };
  }
};

export const patchWithErrorData = async <T>(
  url: string,
  payload: unknown,
  config: AxiosRequestConfig = {}
): Promise<ApiResponse<T>> => {
  try {
    const response = await axiosInstance.patch<T>(url, payload, {
      ...config,
      headers: { Accept: 'application/json', ...(config.headers || {}) },
    });
    return { data: response.data, statusCode: response.status };
  } catch (error: any) {
    return { error: getErrorMessage(error), statusCode: error.response?.status };
  }
};
